import { useEffect, useState } from 'react';

import {
    getEntityAttributeDistinctValues,
    getEntityAttributes,
    type EntityType,
} from '../api/entity-attributes';
import type { FilterParam } from '../components/ui/EntityFilterDropdown';

/**
 * Fetches entity attribute definitions and their distinct values for the given
 * entity type, returning them as ready-to-use FilterParams.
 *
 * Each attribute becomes a param with key `attr:<attributeId>` so the caller
 * can identify attribute-specific selections in FilterValue.
 *
 * Returns an empty array when no attributes with values exist — safe to spread
 * directly into any filter config's params array.
 */
export function useEntityAttributes(entityType: EntityType): {
    attributeParams: FilterParam[];
} {
    const [attributeParams, setAttributeParams] = useState<FilterParam[]>([]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const attributes = await getEntityAttributes(entityType);

                if (cancelled || attributes.length === 0) {
                    setAttributeParams([]);
                    return;
                }

                const params = await Promise.all(
                    attributes.map(async (attr) => {
                        const { values } = await getEntityAttributeDistinctValues(attr.id);
                        if (values.length === 0) return null;
                        return {
                            key: `attr:${attr.id}`,
                            label: attr.name,
                            mode: 'multi' as const,
                            options: values.map((v) => ({ label: v, value: v })),
                        };
                    }),
                );

                if (!cancelled) {
                    setAttributeParams(
                        params.filter((p) => p !== null) as FilterParam[],
                    );
                }
            } catch {
                if (!cancelled) setAttributeParams([]);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [entityType]);

    return { attributeParams };
}

/**
 * Given a FilterValue that may contain `attr:<id>` keys, resolves them to
 * matching entity IDs by calling the backend. Returns null if no attribute
 * filters are active (meaning no restriction should be applied).
 *
 * Import and call this inline inside a data-fetching effect to resolve
 * attribute filters before passing productIds to the main API call.
 */
export async function resolveAttributeFilterEntityIds(
    filterValue: Record<string, string[]>,
    fetchFn: (id: string, values: string[]) => Promise<{ entityIds: string[] }>,
): Promise<string[] | null> {
    const activeAttrEntries = Object.entries(filterValue).filter(
        ([key, values]) => key.startsWith('attr:') && values.length > 0,
    );

    if (activeAttrEntries.length === 0) return null;

    const idSets = await Promise.all(
        activeAttrEntries.map(async ([key, values]) => {
            const attributeId = key.slice('attr:'.length);
            const result = await fetchFn(attributeId, values);
            return new Set(result.entityIds);
        }),
    );

    const [first, ...rest] = idSets;
    const intersected = rest.reduce(
        (acc, set) => new Set([...acc].filter((id) => set.has(id))),
        first,
    );

    return [...intersected];
}
